import {useCallback, useEffect, useMemo, useState} from "react";
import {useOutletContext} from "react-router";
import {Box, Paper, Typography, useMediaQuery, useTheme} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {buildV2BreakdownQueryParams, buildV2QueryParams, hasDateFilters, BREAKDOWN_VIEWS} from "../utils/filters";
import {
    SEGMENTATION_BY_FROM_VIEW,
    SEGMENTATION_CALC_TYPES,
    fetchSegmentationByVMAndVolume,
    mapCalcBreakdownToSegmentationRows,
} from "../utils/segmentation";
import {fetchCalcBreakdownMerged} from "../utils/calcBreakdown";
import {SegmentationMatrixCard} from "../components/SegmentationMatrixCard";
import {SegmentationBubbleChart} from "../components/SegmentationBubbleChart";
import {SegmentationDetailTable} from "../components/SegmentationDetailTable";
import {MeasureDetailGroupBySelect, MEASURE_DETAIL_GROUP_BY_OPTIONS} from "../components/MeasureDetailGroupBySelect";
import {LoadingPanel} from "../utils/loading_spinner";

const ROW_CARD_HEIGHT = 520;

export const SegmentationContainer = () => {
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
    const {filters} = useOutletContext();

    const [breakdownView, setBreakdownView] = useState(BREAKDOWN_VIEWS.CUSTOMERS);
    const [selectedSegment, setSelectedSegment] = useState(null);
    const [segmentationData, setSegmentationData] = useState(null);
    const [loading, setLoading] = useState(false);

    const segmentationBy = SEGMENTATION_BY_FROM_VIEW[breakdownView] || 'account';
    const v2QueryParams = useMemo(() => buildV2QueryParams(filters), [filters]);

    const fetchSegmentation = useCallback(() => {
        if (!hasDateFilters(filters)) {
            setSegmentationData(null);
            return;
        }

        setLoading(true);
        fetchSegmentationByVMAndVolume(v2QueryParams, segmentationBy, (data) => {
            setSegmentationData(data);
            setLoading(false);
        });
    }, [filters, segmentationBy, v2QueryParams]);

    useEffect(() => {
        setSelectedSegment(null);
    }, [breakdownView, filters]);

    useEffect(() => {
        fetchSegmentation();
    }, [fetchSegmentation]);

    const handleLoadChildren = useCallback((parentRow, parentGroupBy, onComplete) => {
        if (parentGroupBy !== 'account_id') {
            onComplete({breakdown: [], groupBy: null});
            return;
        }

        const accountId = Number(parentRow.id);
        if (!Number.isFinite(accountId)) {
            onComplete({breakdown: [], groupBy: null});
            return;
        }

        const queryParams = buildV2BreakdownQueryParams(filters, 'current', {
            accountId,
            breakdownView: BREAKDOWN_VIEWS.PRODUCTS,
        });

        fetchCalcBreakdownMerged(queryParams, SEGMENTATION_CALC_TYPES, (data) => {
            onComplete({
                breakdown: mapCalcBreakdownToSegmentationRows(data?.breakdown || [], SEGMENTATION_CALC_TYPES),
                groupBy: data?.groupBy || 'product_id',
            });
        });
    }, [filters]);

    const filteredBreakdown = useMemo(() => {
        const rows = segmentationData?.breakdown || [];
        if (!selectedSegment) {
            return rows;
        }
        return rows.filter((row) => row.segment === selectedSegment);
    }, [segmentationData?.breakdown, selectedSegment]);

    const detailTitle = MEASURE_DETAIL_GROUP_BY_OPTIONS.find(
        (option) => option.value === breakdownView
    )?.label || 'Customers';

    if (loading && !segmentationData) {
        return <LoadingPanel loading={true} message="Loading segmentation..."/>;
    }

    return (
        <div style={{marginTop: 30, marginBottom: 100, marginLeft: 30, marginRight: 30}}>
            {segmentationData?.currentPeriod && (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 2, marginBottom: 3}}>
                    <Typography variant="caption" color="textSecondary" style={{marginLeft: 'auto', textAlign: 'right', maxWidth: 600}}>
                        Current: {segmentationData.currentPeriod.start} → {segmentationData.currentPeriod.end}
                        {selectedSegment ? ` · Segment: ${selectedSegment}` : ''}
                    </Typography>
                </Box>
            )}

            <Grid container spacing={2} style={{marginBottom: 16}} alignItems="stretch">
                <Grid size={{xs: 12, md: 4}} sx={{maxWidth: {md: 450}, width: '100%', display: 'flex', minHeight: 0}}>
                    <SegmentationMatrixCard
                        matrix={segmentationData?.matrix}
                        breakdownView={breakdownView}
                        selectedSegment={selectedSegment}
                        onSelectSegment={setSelectedSegment}
                        cardHeight={isMdUp ? ROW_CARD_HEIGHT : null}
                    />
                </Grid>
                <Grid size={{xs: 12, md: 8}} sx={{minWidth: 0, display: 'flex', minHeight: 0}}>
                    <SegmentationBubbleChart
                        entities={filteredBreakdown}
                        selectedSegment={selectedSegment}
                        cardHeight={isMdUp ? ROW_CARD_HEIGHT : null}
                    />
                </Grid>
            </Grid>

            <Paper sx={{...theme.palette.contentPanel, borderRadius: 2, p: 2}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2}}>
                    <Typography variant="h6" color="primary">
                        {detailTitle}
                    </Typography>
                    <MeasureDetailGroupBySelect
                        value={breakdownView}
                        onChange={setBreakdownView}
                    />
                </Box>

                <SegmentationDetailTable
                    breakdown={filteredBreakdown}
                    calcConfigs={segmentationData?.calcConfigs}
                    groupBy={segmentationData?.groupBy || 'account_id'}
                    loading={loading}
                    onLoadChildren={handleLoadChildren}
                />
            </Paper>
        </div>
    );
};
